import React, { useState } from 'react';
import { Button, CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

export default function DataUnfoldOperations({ reanalysis, actionControls, t, menuButtonSx, statusSx }) {
  const [anchor, setAnchor] = useState(null);
  const runtime = reanalysis?.runtime;
  const busy = Boolean(reanalysis?.pending || ['processing', 'running', 'queued'].includes(runtime?.status));
  const failed = Boolean(reanalysis?.error || ['failed', 'error'].includes(runtime?.status)
    || ['failed', 'completed_with_errors'].includes(reanalysis?.job?.status));
  const canStart = Boolean(actionControls?.canManage && actionControls?.onReanalyze);
  const onHistory = actionControls?.onOpenReanalysisHistory;
  if (!canStart && !onHistory) return null;
  const close = () => setAnchor(null);
  const openHistory = (event) => { event.stopPropagation(); onHistory?.(); };
  const processed = Number(reanalysis?.job?.processed || 0);
  const statusUnavailable = reanalysis?.error === 'status_unavailable';
  const count = Math.max(Number(runtime?.inProgress || 0), 0);
  const progressLabel = processed > 0 ? t('dag.reanalysis_processed', '{{count}} items processed', { count: processed })
    : count > 0 ? t('dag.reanalysis_processing', 'Reanalyzing {{count}} items', { count })
      : t('dag.reanalysis_waiting', 'Reanalysis in progress');
  return <>
    <Tooltip title={t('dag.dataunfold_options', 'Project options')}>
      <IconButton size="small" className="nodrag nopan" aria-label={t('dag.dataunfold_options', 'Project options')}
        aria-haspopup="menu" aria-expanded={Boolean(anchor)}
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onClick={(event) => { event.stopPropagation(); setAnchor(event.currentTarget); }}
        sx={{ position: 'absolute', right: 0, top: -3, width: 24, height: 24, color: '#5757b0', ...menuButtonSx }}>
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}
      onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}>
      {canStart && <MenuItem disabled={busy} onClick={() => { close(); actionControls.onReanalyze(); }}>
        <ListItemIcon><ReplayRoundedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>{t('dag.reanalyze_data', 'Reanalyze existing data')}</ListItemText>
      </MenuItem>}
      {onHistory && <MenuItem onClick={(event) => { close(); openHistory(event); }}>
        <ListItemIcon><HistoryRoundedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>{t('dag.reanalysis_history', 'Reanalysis history')}</ListItemText>
      </MenuItem>}
    </Menu>
    {(busy || failed) && <Button className="nodrag nopan" size="small" onClick={openHistory}
      onPointerDown={(event) => event.stopPropagation()} disabled={!onHistory}
      onKeyDown={(event) => event.stopPropagation()}
      startIcon={busy && !statusUnavailable ? <CircularProgress size={12} color="inherit" /> : <ErrorOutlineRoundedIcon fontSize="small" />}
      sx={{ mt: 0.25, width: '100%', minWidth: 0, textTransform: 'none', fontSize: 10, lineHeight: 1.3,
        color: busy ? '#5757b0' : '#9b6422', ...statusSx }}>
      <span role="status" aria-live="polite">{statusUnavailable ? t('dag.reanalysis_status_unavailable', 'Tracking unavailable. View history.')
        : busy ? progressLabel : t('dag.reanalysis_attention', 'Review the reanalysis')}</span>
    </Button>}
  </>;
}
